/**
 * What "Add Kong" does with the tiles already in the hand.
 *
 * A kong is always four copies of one tile, and a tile only has four copies, so tiles of that face already
 * entered in the hand can't stay where they are AND be counted again inside the kong. Instead of refusing
 * ("not enough copies left"), the kong takes those tiles over and the app adds however many are missing:
 *
 *   none in the hand   -> 4 new tiles
 *   one in the hand    -> that one becomes part of the kong, 3 new tiles
 *   two in the hand    -> those two, 2 new tiles
 *   three in the hand  -> those three, 1 new tile
 *   four in the hand   -> those four, nothing new (e.g. a photo scan read the whole kong as loose tiles)
 *
 * Pure so the rule can be tested without a browser.
 */

export interface KongPlan {
  /** Copies already in the hand that become part of the kong (they leave the loose tiles). */
  absorb: number;
  /** Brand-new tiles the app adds so the kong has four. */
  add: number;
}

export function kongPlan(copiesInHand: number): KongPlan {
  const absorb = Math.min(4, Math.max(0, Math.floor(copiesInHand)));
  return { absorb, add: 4 - absorb };
}

/** One plain sentence saying what was done, shown after the kong is added. */
export function kongNote(label: string, plan: KongPlan): string {
  if (plan.absorb === 0) return `${label} added: 4 new tiles.`;
  if (plan.add === 0) return `${label} added, using the ${plan.absorb} tiles already in your hand.`;
  const have = plan.absorb === 1 ? '1 tile' : `${plan.absorb} tiles`;
  const added = plan.add === 1 ? '1 new tile' : `${plan.add} new tiles`;
  return `${label} added: ${have} already in your hand + ${added}.`;
}
